import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Gift, Copy, Users, DollarSign, Trophy, 
  Share2, Twitter, Facebook, Mail, Crown,
  CheckCircle, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Referral = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/referral');
      return;
    }
    fetchReferralData();
    fetchLeaderboard();
  }, [isAuthenticated]);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API}/referral/my-code`, getAuthHeader());
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/referral/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const shareOnTwitter = () => {
    const text = `Join me on VelvetStream! Use my code ${referralData?.code} to get 3 days free premium access!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralData?.referral_link)}`, '_blank');
  };

  const shareByEmail = () => {
    const subject = 'Join me on VelvetStream!';
    const body = `Hey! I thought you might enjoy VelvetStream. Use my referral code ${referralData?.code} to get 3 days of free premium access!\n\n${referralData?.referral_link}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleClaimCode = async () => {
    if (!claimCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setClaiming(true);
    try {
      const response = await axios.post(`${API}/referral/claim`, 
        { referral_code: claimCode },
        getAuthHeader()
      );
      toast.success(response.data.message);
      setClaimCode('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim code');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8 bg-[#050505]" data-testid="referral-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase mb-4 text-white"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Referral Program
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Invite friends and earn rewards! Both you and your friends get premium benefits.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 text-center">
            <Users className="w-8 h-8 text-blue-400 mx-auto mb-3" />
            <p className="text-3xl font-bold text-white mb-1">{referralData?.referral_count || 0}</p>
            <p className="text-gray-500 text-sm uppercase tracking-wider">Total Referrals</p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 text-center">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <p className="text-3xl font-bold text-white mb-1">{referralData?.earned_credits || 0}</p>
            <p className="text-gray-500 text-sm uppercase tracking-wider">Credits Earned</p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 text-center">
            <Crown className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-3xl font-bold text-white mb-1">3 Days</p>
            <p className="text-gray-500 text-sm uppercase tracking-wider">Premium Per Referral</p>
          </div>
        </div>

        {/* Your Referral Code */}
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-white/10 rounded-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Your Referral Code
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 bg-black/40 border border-white/20 rounded-lg p-4 flex items-center justify-between">
              <span className="text-2xl font-bold text-white tracking-wider" data-testid="referral-code">
                {referralData?.code}
              </span>
              <button
                onClick={() => copyToClipboard(referralData?.code)}
                className="p-2 hover:bg-white/10 rounded transition-colors"
              >
                <Copy className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <Button 
              onClick={() => copyToClipboard(referralData?.referral_link)}
              className="bg-white text-black hover:bg-gray-200"
            >
              Copy Link
            </Button>
          </div>

          {/* Share Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              onClick={shareOnTwitter}
              className="border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Twitter
            </Button>
            <Button 
              variant="outline"
              onClick={shareOnFacebook}
              className="border-[#4267B2] text-[#4267B2] hover:bg-[#4267B2]/10"
            >
              <Facebook className="w-4 h-4 mr-2" />
              Facebook
            </Button>
            <Button 
              variant="outline"
              onClick={shareByEmail}
              className="border-gray-500 text-gray-400 hover:bg-white/10"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
          </div>
        </div>

        {/* Claim a Code */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Have a Referral Code?</h2>
          <p className="text-gray-400 mb-4">Enter a friend's referral code to get 3 days of premium access!</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="bg-[#1A1A1A] border-white/10 text-white h-12"
              data-testid="claim-code-input"
            />
            <Button 
              onClick={handleClaimCode}
              disabled={claiming}
              className="bg-white text-black hover:bg-gray-200 h-12"
              data-testid="claim-code-btn"
            >
              {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Claim Code'}
            </Button>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-purple-400 font-bold">1</span>
              </div>
              <h3 className="text-white font-medium mb-2">Share Your Code</h3>
              <p className="text-gray-500 text-sm">Share your unique referral code or link with friends</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-pink-400 font-bold">2</span>
              </div>
              <h3 className="text-white font-medium mb-2">Friend Signs Up</h3>
              <p className="text-gray-500 text-sm">When they create an account and use your code</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-amber-400 font-bold">3</span>
              </div>
              <h3 className="text-white font-medium mb-2">Both Get Rewards</h3>
              <p className="text-gray-500 text-sm">You earn 5 credits, they get 3 days premium free!</p>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Top Referrers
            </h2>
            <div className="space-y-3">
              {leaderboard.map((user, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-black/40 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-amber-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-amber-700 text-white' :
                      'bg-[#1A1A1A] text-gray-400'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-white">{user.username || 'Anonymous'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-400">{user.referral_count} referrals</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Referral;
